import React, { useEffect, useState } from "react";
import ProgressTracker from "../../../../utils/ProgressTracker";
import { useCurrencySymbol } from "../../../../utils/currencyUtils";
import DateConversion from "../../../../utils/DateConversion";
import CommaConvert from "../../../../utils/CommaConvert";
import {
  formatAmountComma,
  parseAmountNumber,
} from "../../../../utils/decimalAmountUtils";
import { useSelector } from "react-redux";
import {
  consolidateTrainingExpenseRowsForDisplay,
  isTrainingExpenseDisplayRow,
} from "../../../../utils/trainingOtherExpenseSync";
import {
  canonicalOtherExpenseDescriptionLabel,
  normalizeOtherExpenseDescriptionKey,
} from "../../../../utils/projectAssignDescriptionMerge";

const processHardwareDescriptions = ({
  allDescriptions,
  employees,
  currentEmpId,
}) => {
  const empMap = employees.reduce((acc, e) => {
    acc[e._id] = e;
    return acc;
  }, {});

  const grouped = allDescriptions.reduce((acc, part) => {
    const key = normalizeOtherExpenseDescriptionKey(part.description);
    if (!key) return acc;
    if (!acc[key]) acc[key] = [];
    acc[key].push(part);
    return acc;
  }, {});

  const result = [];

  Object.values(grouped).forEach((parts) => {
    const pmParts = parts.filter((p) =>
      empMap[p.taskownerid]?.customRoles?.includes("project manager")
    );

    const opParts = parts.filter(
      (p) =>
        empMap[p.taskownerid]?.customRoles?.includes("operations") &&
        !empMap[p.taskownerid]?.customRoles?.includes("project manager")
    );

    const highestPM = pmParts.length
      ? pmParts.reduce((a, b) => (b.value > a.value ? b : a))
      : null;

    const highestOP = opParts.length
      ? opParts.reduce((a, b) => (b.value > a.value ? b : a))
      : null;

    // Operations keep their own
    opParts.forEach((p) => {
      if (p.taskownerid === currentEmpId) {
        result.push(p);
      }
    });

    const isPM =
      empMap[currentEmpId]?.customRoles?.includes("project manager");
    if (!isPM) return;

    // Hardware manager Self: one row per description, sum manager + operation amounts
    if (pmParts.length > 0 && opParts.length > 0) {
      const pmOwnEntry =
        pmParts.find((p) => p.taskownerid === currentEmpId) || highestPM;
      if (pmOwnEntry?.taskownerid === currentEmpId) {
        const allParts = [...pmParts, ...opParts];
        result.push({
          ...pmOwnEntry,
          description: canonicalOtherExpenseDescriptionLabel(
            pmOwnEntry.description
          ),
          value: allParts.reduce(
            (sum, p) => sum + parseAmountNumber(p?.value),
            0
          ),
          expenses: allParts.reduce(
            (sum, p) => sum + parseAmountNumber(p?.expenses),
            0
          ),
        });
      }
      return;
    }

    if (!highestPM && highestOP) {
      result.push({
        ...highestOP,
        description: canonicalOtherExpenseDescriptionLabel(
          highestOP.description
        ),
      });
      return;
    }

    if (highestPM && (!highestOP || highestPM.value >= highestOP.value)) {
      if (highestPM.taskownerid === currentEmpId) {
        result.push({
          ...highestPM,
          description: canonicalOtherExpenseDescriptionLabel(
            highestPM.description
          ),
        });
      }
      return;
    }

    if (highestPM && highestOP && highestOP.value > highestPM.value) {
      result.push({
        ...highestOP,
        description: canonicalOtherExpenseDescriptionLabel(
          highestOP.description
        ),
      });
    }
  });

  return result.filter(
    (v, i, self) =>
      i ===
      self.findIndex(
        (x) =>
          normalizeOtherExpenseDescriptionKey(x.description) ===
          normalizeOtherExpenseDescriptionKey(v.description)
      )
  );
};

const ShowCompletedTasks = ({
  employees,
  row,
  projects,
  projectTrackingWorkEntries,
  descriptionProjectTracking,
  hardwareProjectTracking,
  entries,
  tasks,
  showListState
}) => {

  const currency = useCurrencySymbol();
  const USAComma = CommaConvert();
  const { formatDateByCountry } = DateConversion();
  const { mytrainings } = useSelector((state) => state.myTrainings);
  const formatHours = (value) =>
    new Intl.NumberFormat("en-US", {
      maximumFractionDigits: 2,
      minimumFractionDigits: 0,
    }).format(Number(value) || 0);
  const [matchedProjects, setMatchedProjects] = useState([]);

  useEffect(() => {
    if (projects?.length && row) {
      const matchedProjects = [];

      // 1) Prefer match by _id so we use the project that has tracking entries (projectid in workentries)
      // 2) Then by quotationId so the correct project is found when names duplicate
      // 3) Then by TypeService (case-insensitive for e.g. Cloud/cloud) + ProjectName
      const rowType = (row.TypeService || '').toString().trim().toLowerCase();
      const old =
        projects.find((pro) => pro._id === row._id) ||
        (row.quotationId != null
          ? projects.find(
              (pro) =>
                pro.quotationId != null &&
                String(pro.quotationId).trim() === String(row.quotationId).trim()
            )
          : null) ||
        projects.find(
          (pro) =>
            (pro.TypeService || '').toString().trim().toLowerCase() === rowType &&
            pro.ProjectName === row.ProjectName
        );

      if (old) {
        // Use the project from API (old) as newProject so tracking entries by old._id are found
        matchedProjects.push({
          _id: old._id,
          ProjectName: old.ProjectName,
          TypeService: old.TypeService,
          quotationId: row.quotationId ?? old.quotationId,
          oldProject: old,
          newProject: old,
        });
      }

      setMatchedProjects(matchedProjects);
    }
  }, [projects, row]);

  const [allrowData, setAllrowData] = useState([]);

  useEffect(() => {
    if (
      matchedProjects?.length &&
      (entries?.length ||
        tasks?.length ||
        projectTrackingWorkEntries?.length ||
        descriptionProjectTracking?.length)
    ) {
      const updated = matchedProjects.map((proj) => {
        // old matches
        const oldworkEntries = entries.filter(
          (entry) => entry.projectid === proj.oldProject._id
        );
        const oldDescriptions = tasks.filter(
          (task) => task.projectid === proj.oldProject._id
        );

        // new matches
        const newworkEntries = projectTrackingWorkEntries.filter(
          (entry) => entry.projectid === proj.newProject._id
        );
        const newDescriptions = descriptionProjectTracking.filter(
          (desc) => desc.projectid === proj.newProject._id
        );

        const newHardwareParts = hardwareProjectTracking.filter(
          (desc) => desc.projectid === proj.newProject._id
        );

        return {
          _id: proj.newProject._id,
          TypeService: proj.newProject.TypeService,
          ProjectName: proj.newProject.ProjectName,
          Assignedperson: proj.newProject.Assignedperson,
          oldProject: {
            _id: proj.oldProject._id,
            ProjectName: proj.oldProject.ProjectName,
            TypeService: proj.oldProject.TypeService,
            Assignedperson: proj.oldProject.Assignedperson,
            oldworkEntries,
            oldDescriptions,
          },
          newProject: {
            _id: proj.newProject._id,
            ProjectName: proj.newProject.ProjectName,
            TypeService: proj.newProject.TypeService,
            Assignedperson: proj.newProject.Assignedperson,
            newworkEntries,
            newDescriptions,
            newHardwareParts: newHardwareParts,
          },
        };
      });

      setAllrowData(updated); // ✅ update state
    }
  }, [
    matchedProjects,
    entries,
    tasks,
    projectTrackingWorkEntries,
    descriptionProjectTracking,
    hardwareProjectTracking,
  ]);

  const [unifitedList, setUnifitedList] = useState([]);

  useEffect(() => {
    if (allrowData?.length && employees?.length) {
      const mergedData = allrowData.map((project) => {
        const assignedData = project.newProject.Assignedperson.map((person) => {
          // 1️⃣ Find matching employee by email, then by name (so operation person etc. still show)
          let emp = employees.find((e) => e.EmpEmail === person.email);
          if (!emp && person.name) {
            const personNameNorm = (person.name || '').trim().toLowerCase();
            emp = employees.find(
              (e) =>
                `${(e.EmpFirstName || '')} ${(e.EmpLastName || '')}`.trim().toLowerCase() === personNameNorm
            );
          }
          if (!emp && person.email) {
            emp = employees.find(
              (e) => (e.EmpEmail || '').toLowerCase() === (person.email || '').toLowerCase()
            );
          }

          const empid = emp?._id ?? person.empId ?? person.taskownerid ?? null;
          const role = emp?.customRoles?.[0] ?? null;
          const name =
            emp
              ? `${emp.EmpFirstName || ''} ${emp.EmpLastName || ''}`.trim()
              : (person.name || person.email || 'Assigned').trim() || 'Assigned';

          // 3️⃣ Match old/new work entries and descriptions by taskownerid
          const oldworkEntries = (project.oldProject.oldworkEntries || []).filter(
            (entry) => entry.taskownerid === empid
          );
          const newworkEntries = (project.newProject.newworkEntries || []).filter(
            (entry) => entry.taskownerid === empid
          );

          const oldDescriptions = (project.oldProject.oldDescriptions || []).filter(
            (desc) => desc.taskownerid === empid
          );

          let newDescriptions = [];

          if (project.newProject?.TypeService === "Hardware") {
            newDescriptions = processHardwareDescriptions({
              allDescriptions: project.newProject?.newDescriptions || [],
              employees,
              currentEmpId: empid,
            });
          } else {
            newDescriptions = (project.newProject?.newDescriptions || []).filter(
              (desc) => desc.taskownerid === empid
            );
          }

          // Trainings: show as a locked row in Other Expenses for each assignee (manager + operations).
          {
            const resolveRefId = (ref) => {
              if (ref == null || ref === "") return "";
              if (typeof ref === "object")
                return String(ref?._id ?? ref?.id ?? "").trim();
              return String(ref).trim();
            };
            const normKey = (v) => String(v ?? "").trim().toLowerCase();
            const pick = (...vals) =>
              vals.find((v) => v != null && String(v).trim() !== "");

            const trainingsList = Array.isArray(mytrainings?.trainings)
              ? mytrainings.trainings
              : Array.isArray(mytrainings)
                ? mytrainings
                : [];

            const assigneeId = resolveRefId(empid);
            const assigneeEmail = normKey(
              pick(emp?.EmpEmail, person?.email, person?.EmpEmail)
            );
            if (trainingsList.length) {
              const leadLike = project?.newProject?.lead || project?.newProject?.Lead || null;
              const projectIdCandidates = new Set(
                [
                  resolveRefId(
                    pick(
                      project?.newProject?._id,
                      project?.newProject?.id,
                      project?.newProject?.ProjectID,
                      project?.newProject?.projectid,
                      project?.newProject?.projectId
                    )
                  ),
                  resolveRefId(pick(leadLike?._id, leadLike?.id)),
                ]
                  .map((x) => normKey(x))
                  .filter(Boolean)
              );
              const quotationIdCandidates = new Set(
                [
                  pick(
                    project?.newProject?.quotationId,
                    project?.newProject?.quotationID,
                    project?.newProject?.quotationid,
                    project?.quotationId
                  ),
                  pick(
                    leadLike?.quotationId,
                    leadLike?.quotationID,
                    leadLike?.quotationid
                  ),
                ]
                  .map((x) => String(x || "").trim())
                  .filter(Boolean)
              );
              const projectNameCandidates = new Set(
                [
                  pick(project?.newProject?.ProjectName, project?.newProject?.projectname),
                  pick(leadLike?.ProjectName, leadLike?.projectname),
                ]
                  .map((x) => normKey(x))
                  .filter(Boolean)
              );
              const leadIdCandidates = new Set(
                [
                  pick(
                    project?.newProject?.leadid,
                    project?.newProject?.leadId,
                    project?.newProject?.LeadId,
                    project?.newProject?.LeadID
                  ),
                  pick(leadLike?.leadid, leadLike?.leadId, leadLike?.LeadId, leadLike?.LeadID),
                ]
                  .map((x) => String(x || "").trim())
                  .filter(Boolean)
              );

              const matched = trainingsList.filter((t) => {
                const assignedToRaw = t?.assignedto ?? t?.assignedTo;
                const assignedToId = resolveRefId(assignedToRaw);
                const assignedToEmail =
                  typeof assignedToRaw === "string" && assignedToRaw.includes("@")
                    ? normKey(assignedToRaw)
                    : normKey(t?.assignedToEmail ?? "");

                const matchesAssignee =
                  (assigneeId && assignedToId && assignedToId === assigneeId) ||
                  (assigneeEmail && assignedToEmail && assignedToEmail === assigneeEmail);
                if (!matchesAssignee) return false;

                const tProjectId = normKey(
                  resolveRefId(t?.projectid ?? t?.projectId ?? t?.project)
                );
                const tQuotationId = String(
                  t?.quotationId ?? t?.quotationID ?? t?.quotationid ?? ""
                ).trim();
                const tProjectName = normKey(t?.projectname ?? t?.ProjectName);
                const tLeadId = String(t?.leadid ?? t?.leadId ?? t?.LeadId ?? "").trim();

                return (
                  (tProjectId && projectIdCandidates.has(tProjectId)) ||
                  (tQuotationId && quotationIdCandidates.has(tQuotationId)) ||
                  (tProjectName && projectNameCandidates.has(tProjectName)) ||
                  (tLeadId && leadIdCandidates.has(tLeadId))
                );
              });

              const normalized = matched.map((t) => {
                const amount = Number(
                  String(t?.trainingcost ?? t?.trainingCost ?? 0).replace(/,/g, "")
                );
                const notes = t?.notes ?? t?.note ?? "";
                return {
                  amount: Number.isFinite(amount) ? amount : 0,
                  notes: String(notes || "").trim(),
                };
              });
              const total = normalized.reduce(
                (sum, x) => sum + (Number(x.amount) || 0),
                0
              );
              const joinedNotes = Array.from(
                new Set(normalized.map((x) => x.notes).filter(Boolean))
              ).join("; ");
              const hasTrainingAlready = (newDescriptions || []).some(
                (d) => isTrainingExpenseDisplayRow(d)
              );
              if (!hasTrainingAlready && (total > 0 || joinedNotes)) {
                newDescriptions = [
                  ...newDescriptions,
                  {
                    _id: `training_cost_${assigneeId || assigneeEmail}`,
                    taskownerid: assigneeId || assigneeEmail,
                    description: "Training Cost",
                    value: total,
                    notes: joinedNotes,
                    isTaskComplete: true,
                    expenses: total,
                  },
                ];
              }
              newDescriptions = consolidateTrainingExpenseRowsForDisplay(newDescriptions);
            }
          }

          const newHardwareParts = (project.newProject.newHardwareParts || []).filter(
            (desc) => desc.taskownerid === empid
          );

          // 4️⃣ Build unified structure (include even when emp not found so name still shows)
          return {
            TypeService: project.TypeService,
            name,
            empid,
            role,
            oldworkEntries,
            newworkEntries,
            oldDescriptions,
            newDescriptions,
            newHardwareParts,
          };
        }).filter((a) => a != null); // keep all assigned persons

        // For Cloud/non-Hardware: include any task owner from work entries who isn't in Assignedperson yet (so their entries show)
        const assignedEmpIds = new Set(assignedData.map((a) => a.empid).filter(Boolean));
        const allNewWorkEntries = project.newProject.newworkEntries || [];
        const missingOwnerIds = [...new Set(allNewWorkEntries.map((e) => e.taskownerid))].filter(
          (id) => id && !assignedEmpIds.has(id)
        );
        missingOwnerIds.forEach((tid) => {
          const emp = employees.find((e) => e._id === tid);
          assignedData.push({
            TypeService: project.TypeService,
            name: emp
              ? `${(emp.EmpFirstName || '').trim()} ${(emp.EmpLastName || '').trim()}`.trim() || emp.EmpEmail || 'Assigned'
              : 'Assigned',
            empid: tid,
            role: emp?.customRoles?.[0] ?? null,
            oldworkEntries: [],
            newworkEntries: allNewWorkEntries.filter((e) => e.taskownerid === tid),
            oldDescriptions: [],
            newDescriptions: (project.newProject?.newDescriptions || []).filter((d) => d.taskownerid === tid),
            newHardwareParts: [],
          });
        });

        assignedData.sort((a, b) => (a.role === "project manager" ? -1 : b.role === "project manager" ? 1 : 0));

        if (project.newProject?.TypeService === "Hardware") {

          // 1️⃣ Create employee map for quick lookup
          const empMap = assignedData.reduce((acc, e) => {
            acc[e.empid] = e;
            return acc;
          }, {});

          // 2️⃣ Collect ALL newHardwareParts
          const allNewHardwareParts = assignedData.flatMap(
            e => e.newHardwareParts || []
          );

          // 3️⃣ Group by assemblypartdescription
          const groupedByAssembly = allNewHardwareParts.reduce((acc, part) => {
            const key = part.assemblepartdescription;
            if (!acc[key]) acc[key] = [];
            acc[key].push(part);
            return acc;
          }, {});

          // 4️⃣ Reset processed hardware array
          assignedData.forEach(e => {
            e.hardwarePartsOngoing = [];
          });

          // 5️⃣ Find Project Manager
          const pmEmp = assignedData.find(
            e => e.role === "project manager"
          );

          // 6️⃣ Apply rules
          Object.entries(groupedByAssembly).forEach(([desc, parts]) => {
            if (!pmEmp) return;

            const pmParts = parts.filter(
              p => empMap[p.taskownerid]?.role === "project manager"
            );

            const opParts = parts.filter(
              p => empMap[p.taskownerid]?.role === "operations"
            );

            // ✅ Operations always keep their own parts
            opParts.forEach(part => {
              const opEmp = empMap[part.taskownerid];
              if (opEmp) opEmp.hardwarePartsOngoing.push(part);
            });

            // ✅ Decide PM entry
            if (pmParts.length && opParts.length) {

              const highestPM = pmParts.reduce((a, b) =>
                Number(b.quantity) > Number(a.quantity) ? b : a
              );

              const highestOP = opParts.reduce((a, b) =>
                Number(b.quantity) > Number(a.quantity) ? b : a
              );

              pmEmp.hardwarePartsOngoing.push(
                Number(highestOP.quantity) > Number(highestPM.quantity)
                  ? { ...highestOP }
                  : { ...highestPM }
              );

            } else if (!pmParts.length && opParts.length) {
              // 🔥 OP only → clone to PM
              const highestOP = opParts.reduce((a, b) =>
                Number(b.quantity) > Number(a.quantity) ? b : a
              );

              pmEmp.hardwarePartsOngoing.push({ ...highestOP });

            } else if (pmParts.length) {

              const highestPM = pmParts.reduce((a, b) =>
                Number(b.quantity) > Number(a.quantity) ? b : a
              );

              pmEmp.hardwarePartsOngoing.push({ ...highestPM });
            }
          });

          // 7️⃣ OPTIONAL: replace original newHardwareParts with processed data
          assignedData.forEach(e => {
            e.newHardwareParts = e.hardwarePartsOngoing;
          });
        }

        return {
          ...project,
          AssignedPersonData: assignedData, // ✅ merged data per employee
        };
      });
      setUnifitedList(mergedData);
    }
  }, [allrowData, employees, mytrainings]);

  const [currentProject, setCurrentProject] = useState({})
  const [clickedIdPerson, setClickedIdPerson] = useState(null)

  useEffect(() => {
    if (!unifitedList?.length) return;

    // find the first project manager
    for (const project of unifitedList) {
      const manager = project.AssignedPersonData?.find(
        (p) => p.role === "project manager"
      );
      if (manager) {
        setCurrentProject(manager);
        setClickedIdPerson(manager.empid);
        break; // stop once found
      }
    }
  }, [unifitedList]); // run when data loads

  return (
    <div className="flex w-[100%] border-b">
      <div className=" w-[20%] flex justify-center items-start p-2">
        {unifitedList?.map((project, projectIndex) => (
          <div key={projectIndex} className="w-full flex">
            <div className="w-[100%] flex flex-col ">
              {project.AssignedPersonData?.map((proj, index) => {
                return (
                  <div key={index} className="w-[100%] flex flex-col  gap-2 mb-2 ">
                    <button
                      type="button"
                      onClick={() => {
                        setCurrentProject(currentProject === proj ? null : proj);
                        setClickedIdPerson(clickedIdPerson === proj.empid ? null : proj.empid);
                      }}
                      className={` w-full h-[1.5rem] border text-[12px] rounded-md shadow-md text-start px-1 overflow-hidden truncate
                         ${clickedIdPerson === proj.empid
                          ? "bg-blue-500 text-white border-blue-500"
                          : "bg-white border-gray-400"
                        }`}
                      title={proj.role === "project manager" ? "Self" : proj.name} // full name on hover
                    >
                      {proj.role === "project manager" ? "Self" : proj.name}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div
        className="w-[80%] p-2"
      >
        <div className="w-[100%]   border break-words text-wrap">
          {currentProject?.TypeService?.toLowerCase().includes("hardware")
            ? showListState === "tasktable" && (
              <>
                <div className="w-full max-h-[24rem] overflow-y-auto overflow-x-hidden">
                  {/* Header */}
                  <div className="flex sticky top-0 bg-[#e8e8e8] text-black text-[10px] font-semibold break-all whitespace-normal z-[2399]">
                    <div className="p-1 w-[14%] text-center break-words whitespace-normal">
                      Part Name
                    </div>
                    <div className="p-1 border w-[10%] text-center break-words whitespace-normal">
                      Quantity
                    </div>{" "}
                    <div className="p-1 border w-[15%] break-words whitespace-normal">Total Price ({currency})</div>
                    <div className="p-1 border w-[15%] text-center break-words whitespace-normal">
                      Start Date
                    </div>
                    <div className="p-1 border w-[15%] break-words whitespace-normal">End Date</div>
                    <div className="p-1 border w-[31%] text-center">
                      Tracker
                    </div>
                  </div>

                  <div
                    className="flex text-center text-[10px] break-all whitespace-normal max-h-14"
                  >
                    <div className="p-1 text-center border-b border-[#dee2e6] w-[14%]">
                      {"Total"}
                    </div>

                    <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center">
                      {USAComma(currentProject?.newHardwareParts?.reduce((sum, item) => sum + (item?.quantity || 0), 0))}
                    </div>
                    <div className="p-1 border-b border-[#dee2e6] w-[15%] text-center">
                      {USAComma(currentProject?.newHardwareParts?.reduce((sum, item) => sum + (item?.totalprice || 0), 0))}
                    </div>
                    <div className="p-1 border-b border-[#dee2e6] w-[15%] text-center">
                    </div>
                    <div className="p-1 border-b border-[#dee2e6] w-[15%] text-center">
                    </div>
                    <div className="p-2.5 border-b border-[#dee2e6] w-[31%] text-center">
                      {(() => {
                        const allEntries = currentProject?.newHardwareParts?.length;
                        const completedEntriesCount = currentProject?.newHardwareParts?.filter(entry => entry.isTaskComplete).length;
                        const trackerPercentage = allEntries > 0 ? (completedEntriesCount / allEntries) * 100 : 0;
                        return <ProgressTracker value={trackerPercentage} />;
                      })()}
                    </div>

                  </div>

                  {/* Rows */}
                  {currentProject?.newHardwareParts?.length > 0 ? (
                    currentProject?.newHardwareParts?.map((entry, index) => (
                      <div
                        key={entry}
                        className="flex text-center text-[10px] break-all whitespace-normal max-h-14"
                      >
                        <div className="p-1 border-b border-[#dee2e6] w-[14%]"
                          title={`${entry.assemblepartdescription}`}
                        >
                          {entry.assemblepartdescription}
                        </div>
                        <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center">
                          {entry.quantity || ""}
                        </div>
                        <div className="p-1 border-b border-[#dee2e6] w-[15%] text-center">
                          {USAComma(entry.totalprice) || ""}
                        </div>
                        <div className="p-1 border-b border-[#dee2e6] w-[15%] text-center">
                          {entry.startdate
                            ? formatDateByCountry(entry.startdate)
                            : ""}                         </div>
                        <div className="p-1 border-b border-[#dee2e6] w-[15%] text-center">
                          {entry.enddate
                            ? formatDateByCountry(entry.enddate)
                            : ""}                         </div>
                        <div className="p-2.5 border-b border-[#dee2e6] w-[31%] text-center">
                          <ProgressTracker value={entry.isTaskComplete ? 100 : 0} />
                        </div>

                      </div>
                    ))
                  ) : (
                    <div className="flex text-[14px] break-all whitespace-normal max-h-14 p-1">
                      <p className="text-red-500 text-center">
                        {" "}
                        No Tracking Task Entries Found
                      </p>
                    </div>
                  )}
                </div>
              </>
            )
            : showListState === "tasktable" && (
              <div className="w-full max-h-[24rem] overflow-y-auto overflow-x-hidden">
                <div className="sticky top-0 z-[2399] flex bg-[#e8e8e8] text-black text-[10px] font-semibold break-all whitespace-normal">
                  <div className="p-1 w-[14%] text-center break-words whitespace-normal">
                    Tasks
                  </div>
                  <div className="p-1 border w-[11%] text-center break-words whitespace-normal">
                    Hours Assigned
                  </div>{" "}
                  <div className="p-1 border w-[12%] text-center break-words whitespace-normal">
                    Hours Spent
                  </div>{" "}
                  <div className="p-1 border w-[15%] text-center break-words whitespace-normal">
                    Start Date
                  </div>
                  <div className="p-1 w-[15%] break-words whitespace-normal">End Date</div>
                  <div className="p-1 w-[10%] break-words whitespace-normal">Notes</div>
                  <div className="p-1 w-[25%] text-center break-words whitespace-normal">
                    Tracker
                  </div>
                </div>

                <div
                  className="flex text-center text-[10px] break-all whitespace-normal max-h-14"
                >
                  <div className="p-1 text-center border-b border-[#dee2e6] w-[14%]">
                    {"Total"}
                  </div>
                  <div className="p-1 border-b border-[#dee2e6] w-[11%]">
                    {formatHours(
                      currentProject?.newworkEntries?.reduce(
                        (sum, item) => sum + (item?.hoursassigned || 0),
                        0
                      )
                    )}
                  </div>
                  <div className="p-1 border-b border-[#dee2e6] w-[12%] text-center">
                    {formatHours(
                      currentProject?.newworkEntries?.reduce(
                        (sum, item) => sum + (item?.hoursspent || 0),
                        0
                      )
                    )}
                  </div>
                  <div className="p-1 border-b border-[#dee2e6] w-[15%] text-center">
                  </div>
                  <div className="p-1 border-b border-[#dee2e6] w-[15%] text-center">
                  </div>
                  <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center">
                  </div>
                  <div className="p-2.5 border-b border-[#dee2e6] w-[25%] text-center">
                    {currentProject?.newworkEntries?.every((entry) => entry?.isTaskComplete) ? (
                      <>
                        <ProgressTracker value={100} />
                      </>
                    ) : (
                      <>
                        {(() => {
                          const hoursAssigned = currentProject?.newworkEntries?.reduce((sum, item) => sum + (item?.hoursassigned || 0), 0);
                          const hoursSpent = currentProject?.newworkEntries?.reduce((sum, item) => sum + (item?.hoursspent || 0), 0);
                          const trackerPercentage = hoursAssigned > 0 ? (hoursSpent / hoursAssigned) * 100 : 0;
                          return <ProgressTracker value={trackerPercentage} />;
                        })()}
                      </>
                    )}
                  </div>


                </div>
                {currentProject?.newworkEntries?.length > 0 ? (
                  currentProject?.newworkEntries?.map((entry, index) => (
                    <div
                      key={entry}
                      className="flex text-center text-[10px] break-all whitespace-normal max-h-14"
                    >
                      <div className="p-1 border-b border-[#dee2e6] w-[14%] break-words whitespace-normal"
                        title={`${entry.task}`}
                      >
                        {entry.task}
                      </div>
                      <div className="p-1 border-b border-[#dee2e6] w-[11%]">
                        {entry.hoursassigned !== undefined &&
                        entry.hoursassigned !== null &&
                        entry.hoursassigned !== ""
                          ? formatHours(entry.hoursassigned)
                          : ""}
                      </div>
                      <div className="p-1 border-b border-[#dee2e6] w-[12%] text-center">
                        {entry.hoursspent
                          ? formatHours(entry.hoursspent)
                          : ""}
                      </div>
                      <div className="p-1 border-b border-[#dee2e6] w-[15%] text-center">
                        {entry.startdate
                          ? formatDateByCountry(entry.startdate)
                          : ""}                      </div>
                      <div className="p-1 border-b border-[#dee2e6] w-[15%] text-center">
                        {entry.enddate
                          ? formatDateByCountry(entry.enddate)
                          : ""}                      </div>
                      <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center"
                        title={`${entry.notes}`}
                      >
                        {entry.notes}
                      </div>
                      <div className="p-2.5 border-b border-[#dee2e6] w-[25%] text-center">
                        {entry?.isTaskComplete ? (
                          <ProgressTracker
                            value={100}
                          />
                        ) : (
                          <ProgressTracker
                            value={Math.max(
                              0,
                              Number(entry?.hoursassigned) > 0
                                ? ((Number(entry?.hoursspent) || 0) /
                                  Number(entry?.hoursassigned)) *
                                100
                                : 0
                            )}
                          />
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="flex text-[14px] break-all whitespace-normal max-h-14 p-1">
                    <p className="text-red-500 text-center">
                      {" "}
                      No Tracking Task Entries Found
                    </p>
                  </div>
                )}
              </div>
            )}

          {showListState === "otherexpenses" &&
            (currentProject?.TypeService?.toLowerCase().includes("hardware") ? (
              // (currentProject?.TypeService === "Hardware" ? (
              <div className="w-full max-h-[24rem] overflow-y-auto overflow-x-hidden">
                <div className="sticky top-0 z-[2399] flex text-center bg-[#e8e8e8] text-black text-[10px] font-semibold break-all whitespace-normal">
                  <div className="p-1 w-[14%] text-center break-words whitespace-normal">
                    Description
                  </div>
                  <div className="p-1 w-[10%] text-center break-words whitespace-normal">
                    Amount ({currency})
                  </div>

                  <div className="p-1 w-[15%] text-center break-words whitespace-normal">
                    Start Date
                  </div>
                  <div className="p-1 w-[15%] break-words whitespace-normal">End Date</div>
                  <div className="p-1 w-[10%] text-center break-words whitespace-normal">
                    Notes
                  </div>{" "}
                  <div className="p-1 w-[11%] text-center break-words whitespace-normal">
                    Expenses ({currency})
                  </div>{" "}
                  <div className="p-1 w-[25%] text-center">
                    Tracker
                  </div>
                </div>

                <div
                  className="flex text-center text-[10px] break-all whitespace-normal max-h-14"
                >
                  <div className="p-1 text-center border-b border-[#dee2e6] w-[14%]">
                    {"Total"}
                  </div>
                  <div className="p-1 border-b border-[#dee2e6] w-[10%]">
                    {formatAmountComma(currentProject?.newDescriptions?.reduce((sum, item) => sum + parseAmountNumber(item?.value), 0))}
                  </div>
                  <div className="p-1 border-b border-[#dee2e6] w-[15%] text-center">
                  </div>
                  <div className="p-1 border-b border-[#dee2e6] w-[15%] text-center">
                  </div>
                  <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center">
                  </div>
                  <div className="p-1 border-b border-[#dee2e6] w-[11%] text-center">
                    {formatAmountComma(currentProject?.newDescriptions?.reduce((sum, item) => sum + parseAmountNumber(item?.expenses), 0))}
                  </div>
                  <div className="p-2.5 border-b border-[#dee2e6] w-[25%] text-center">
                    {currentProject?.newDescriptions?.every((entry) => entry?.isTaskComplete) ? (
                      <>
                        <ProgressTracker value={100} />
                      </>
                    ) : (
                      <>
                        {(() => {
                          const allEntries = currentProject?.newDescriptions?.length;
                          const completedEntriesCount = currentProject?.newDescriptions?.filter(entry => entry.isTaskComplete).length;
                          const trackerPercentage = allEntries > 0 ? (completedEntriesCount / allEntries) * 100 : 0;
                          return <ProgressTracker value={trackerPercentage} />;
                        })()}
                      </>
                    )}
                  </div>

                </div>

                {/* Rows */}
                {currentProject?.newDescriptions?.length > 0 ? (
                  currentProject?.newDescriptions?.map((entry, index) => (
                    <div
                      key={entry}
                      className="flex text-center text-[10px] break-all whitespace-normal max-h-14"
                    >
                      <div className="p-1 border-b border-[#dee2e6] w-[14%] "
                        title={`${entry.description}`}
                      >
                        {entry.description}
                      </div>
                      <div className="p-1 border-b border-[#dee2e6] w-[10%]">
                        {formatAmountComma(entry.value)}
                      </div>
                      <div className="p-1 border-b border-[#dee2e6] w-[15%] text-center">
                        {entry.startdate
                          ? formatDateByCountry(entry.startdate)
                          : ""}
                      </div>
                      <div className="p-1 border-b border-[#dee2e6] w-[15%] text-center">
                        {entry.enddate
                          ? formatDateByCountry(entry.enddate)
                          : ""}
                      </div>
                      <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center "
                        title={`${entry.notes}`}
                      >
                        {entry.notes || ""}
                      </div>
                      <div className="p-1 border-b border-[#dee2e6] w-[11%]">
                        {formatAmountComma(entry.expenses)}
                      </div>
                      <div className="p-2.5 border-b border-[#dee2e6] w-[25%] text-center">
                        <ProgressTracker value={entry.isTaskComplete ? 100 : 0} />
                      </div>

                    </div>
                  ))
                ) : (
                  <div className="flex text-[14px] break-all whitespace-normal max-h-14 p-1">
                    <p className="text-red-500 text-center">
                      {" "}
                      No Tracking Task Entries Found
                    </p>
                  </div>
                )}
              </div>

            ) : (
              <div className="w-full max-h-[24rem] overflow-y-auto overflow-x-hidden">
                <div className="sticky top-0 z-[2399] flex bg-[#e8e8e8] text-center text-black text-[10px] font-semibold break-all whitespace-normal">
                  <div className="p-1 w-[14%] text-center">
                    Description
                  </div>
                  <div className="p-1 w-[10%] text-center">
                    Amount ({currency})
                  </div>
                  <div className="p-1 w-[15%] text-center break-words whitespace-normal">
                    Start
                    Date
                  </div>
                  <div className="p-1 w-[15%] break-words whitespace-normal">End Date</div>
                  <div className="p-1 w-[10%] break-words whitespace-normal">Notes</div>
                  <div className="p-1 w-[11%] text-center break-words whitespace-normal">
                    Expenses ({currency})
                  </div>
                  <div className="p-1 w-[25%] text-center">
                    Tracker
                  </div>
                </div>
                <div
                  className="flex text-center text-[10px] break-all whitespace-normal max-h-14"
                >
                  <div className="p-1 text-center border-b border-[#dee2e6] w-[14%]">
                    {"Total"}
                  </div>
                  <div className="p-1 border-b border-[#dee2e6] w-[10%]">
                    {formatAmountComma(currentProject?.newDescriptions?.reduce((sum, item) => sum + parseAmountNumber(item?.value), 0))}
                  </div>
                  <div className="p-1 border-b border-[#dee2e6] w-[15%] text-center">
                  </div>
                  <div className="p-1 border-b border-[#dee2e6] w-[15%] text-center">
                  </div>
                  <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center">
                  </div>
                  <div className="p-1 border-b border-[#dee2e6] w-[11%] text-center">
                    {formatAmountComma(currentProject?.newDescriptions?.reduce((sum, item) => sum + parseAmountNumber(item?.expenses), 0))}
                  </div>
                  <div className="p-2.5 border-b border-[#dee2e6] w-[25%] text-center">
                    {currentProject?.newDescriptions?.every((entry) => entry?.isTaskComplete) ? (
                      <>
                        <ProgressTracker value={100} />
                      </>
                    ) : (
                      <>
                        {(() => {
                          const allEntries = currentProject?.newDescriptions?.length;
                          const completedEntriesCount = currentProject?.newDescriptions?.filter(entry => entry.isTaskComplete).length;
                          const trackerPercentage = allEntries > 0 ? (completedEntriesCount / allEntries) * 100 : 0;
                          return <ProgressTracker value={trackerPercentage} />;
                        })()}
                      </>
                    )}
                  </div>
                </div>
                {currentProject?.newDescriptions?.length > 0 ? (
                  currentProject?.newDescriptions?.map((entry, index) => (
                    <div
                      key={entry}
                      className="flex text-center text-[10px] break-all whitespace-normal max-h-14"
                    >
                      <div className="p-1 border-b border-[#dee2e6] w-[14%] break-words whitespace-normal"
                        title={`${entry.description}`}
                      >
                        {entry.description || ""}
                      </div>
                      <div className="p-1 border-b border-[#dee2e6] w-[10%]">
                        {formatAmountComma(entry.value) || ""}
                      </div>

                      <div className="p-1 border-b border-[#dee2e6] w-[15%] text-center">
                        {entry.startdate
                          ? formatDateByCountry(entry.startdate)
                          : ""}
                      </div>
                      <div className="p-1 border-b border-[#dee2e6] w-[15%] text-center">
                        {entry.enddate
                          ? formatDateByCountry(entry.enddate)
                          : ""}
                      </div>
                      <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center break-words whitespace-normal"
                        title={`${entry.notes}`}
                      >
                        {entry.notes || ""}
                      </div>
                      <div className="p-1 border-b border-[#dee2e6] w-[11%]">
                        {formatAmountComma(entry.expenses) || ""}
                      </div>
                      <div className="p-2.5 border-b border-[#dee2e6] w-[25%] text-center">
                        <ProgressTracker value={entry.isTaskComplete ? 100 : 0} />
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="flex text-[14px] break-all whitespace-normal max-h-14 p-1">
                    <p className="text-red-500 text-center">
                      {" "}
                      No Tracking Task Entries Found
                    </p>
                  </div>
                )}
              </div>
            ))}
        </div>

      </div>


    </div>
  );
};

export default ShowCompletedTasks;
